import { useEditor } from '@/components/Editor/EditorContext';
import * as Sentry from '@sentry/react';
import { SectionData, SectionVariant } from '@superfunnel/types';
import Modal from '@superfunnel/ui-core/Modal';
import FieldTitle from '@superfunnel/ui-core/v2/FieldTitle';
import Input from '@superfunnel/ui-core/v2/Input';
import Label from '@superfunnel/ui-core/v2/Label';
import Switch from '@superfunnel/ui-core/v2/Switch';
import {
  ToggleGroup,
  ToggleGroupItem,
} from '@superfunnel/ui-core/v2/ToggleGroup';
import { useCallback, useContext, useState } from 'react';
import { UserContext } from '../contexts/UserContext';
import {
  addSectionToAccountCatalog,
  addSectionToGlobalCatalog,
} from '../services/SectionsService';

interface AddToCatalogSectionConfig {
  name: string;
  level: 'global' | 'account';
  saveContent: boolean;
  saveImages: boolean;
}

const createDefaultAddToCatalogSectionConfig =
  (): AddToCatalogSectionConfig => ({
    name: '',
    level: 'account',
    saveContent: true,
    saveImages: true,
  });

interface Props {
  section?: SectionData;
  open: boolean;
  onClose: () => void;
}

export default function AddSectionToCatalogModal({
  section,
  open,
  onClose,
}: Props) {
  const { selectedAccount } = useContext(UserContext);
  const { dynamicPage } = useEditor();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();

  const [addToCatalogSectionConfig, setAddToCatalogSectionConfig] =
    useState<AddToCatalogSectionConfig>(createDefaultAddToCatalogSectionConfig);

  const _onClose = useCallback(() => {
    setError(undefined);
    setLoading(false);
    setAddToCatalogSectionConfig(createDefaultAddToCatalogSectionConfig());
    onClose();
  }, [onClose]);

  const onAddClick = useCallback(() => {
    if (!open) return;

    if (!section || !section.type || !section.variant) {
      setError('Something went wrong. Please try again.');
      return;
    }

    if (!selectedAccount || !dynamicPage) {
      setError('Something went wrong. Please try again.');
      return;
    }

    const newSection: Partial<SectionData> = {
      ...section,
    };

    if (!addToCatalogSectionConfig.saveContent) {
      delete newSection.title;
      delete newSection.tagline;
      delete newSection.subtitle;

      if ('text' in newSection) {
        delete newSection.text;
      }

      if ('description' in newSection) {
        delete newSection.description;
      }

      if ('items' in newSection) {
        delete newSection.items;
      }

      if ('features' in newSection) {
        delete newSection.features;
      }

      if ('testimonials' in newSection) {
        delete newSection.testimonials;
      }

      if ('pressMentions' in newSection) {
        delete newSection.pressMentions;
      }

      if ('logos' in newSection) {
        delete newSection.logos;
      }

      if ('cta' in newSection) {
        delete newSection.cta;
      }
    }

    if (!addToCatalogSectionConfig.saveImages) {
      if ('image' in newSection) {
        delete newSection.image;
      }
    }

    if ('formData' in newSection) {
      delete newSection.formData;
    }

    if ('formId' in newSection) {
      delete newSection.formId;
    }

    setLoading(true);
    const promise =
      addToCatalogSectionConfig.level === 'account'
        ? addSectionToAccountCatalog(
            selectedAccount.id,
            section.type,
            newSection as SectionVariant,
          )
        : addSectionToGlobalCatalog(section.type, newSection as SectionVariant);
    promise
      .then(() => {
        _onClose();
      })
      .catch((e) => {
        Sentry.captureException(e);
        setError(e.message);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    open,
    section,
    selectedAccount,
    dynamicPage,
    addToCatalogSectionConfig.saveContent,
    addToCatalogSectionConfig.saveImages,
    addToCatalogSectionConfig.level,
    _onClose,
  ]);

  return (
    <Modal
      open={open}
      onClose={_onClose}
      actions={[
        {
          title: 'Cancel',
          type: 'secondary',
          disabled: loading,
          onClick: _onClose,
        },
        {
          title: 'Create',
          type: 'primary',
          loading: loading,
          onClick: onAddClick,
        },
      ]}
      title="Add Section to Catalog"
    >
      <div className="mt-0 flex flex-col gap-4">
        <Input
          title="Name (Optional)"
          value={addToCatalogSectionConfig.name}
          onChange={(e) =>
            setAddToCatalogSectionConfig({
              ...addToCatalogSectionConfig,
              name: e.target.value,
            })
          }
        />

        <div>
          <FieldTitle className="mb-2" title={'Save to'} />

          <ToggleGroup
            type="single"
            variant="outline"
            value={addToCatalogSectionConfig.level}
            onValueChange={(value: AddToCatalogSectionConfig['level']) => {
              if (!value) return;
              setAddToCatalogSectionConfig({
                ...addToCatalogSectionConfig,
                level: value,
              });
            }}
          >
            <ToggleGroupItem value="global">Global</ToggleGroupItem>
            <ToggleGroupItem value="account">Account</ToggleGroupItem>
          </ToggleGroup>
        </div>

        <div className="flex items-center space-x-3">
          <Switch
            checked={addToCatalogSectionConfig.saveContent}
            onCheckedChange={(checked) => {
              setAddToCatalogSectionConfig({
                ...addToCatalogSectionConfig,
                saveContent: checked,
              });
            }}
          />
          <Label className="text-sm font-medium">Save Content</Label>
        </div>

        <div className="flex items-center space-x-3">
          <Switch
            checked={addToCatalogSectionConfig.saveImages}
            onCheckedChange={(checked) => {
              setAddToCatalogSectionConfig({
                ...addToCatalogSectionConfig,
                saveImages: checked,
              });
            }}
          />
          <Label className="text-sm font-medium">Save Images</Label>
        </div>
      </div>
      {error ? (
        <p className="mt-4 w-full text-end text-sm text-red-600">{error}</p>
      ) : (
        <></>
      )}
    </Modal>
  );
}
